/**
 * Geração das seções iniciais do documento:
 * - Seção B: NORMAS E ABREVIAÇÕES
 * - Seção C: INFORMAÇÕES GERAIS PARA APRECIAÇÃO DE RISCO
 */

import { escapeLatex } from '../core/latex-utils';

/**
 * Formata uma norma com o nome em negrito e descrição normal
 */
function formatarNorma(norma: string): string {
  // Extrair o nome da norma (até o primeiro " - " ou ":")
  const match = norma.match(/^([^:]+?)(?:\s*-\s*|\s*:\s*)(.+)$/);
  if (match) {
    const nomeNorma = match[1].trim();
    const descricao = match[2].trim();
    return `\\textbf{${escapeLatex(nomeNorma)}} - ${escapeLatex(descricao)}`;
  }
  // Se não encontrar o padrão, colocar tudo em negrito até o primeiro " - "
  const partes = norma.split(' - ', 2);
  if (partes.length === 2) {
    return `\\textbf{${escapeLatex(partes[0].trim())}} - ${escapeLatex(partes[1].trim())}`;
  }
  // Fallback: colocar tudo em negrito
  return `\\textbf{${escapeLatex(norma)}}`;
}

/**
 * Gera um tipo de perigo com duas colunas lado a lado (Potenciais consequências e Origem)
 */
function gerarTipoPerigo(numero: number, titulo: string, potenciaisConsequencias: string[], origem: string[]): string {
  const potenciaisItems = potenciaisConsequencias.map(item => `    \\item ${escapeLatex(item)}`).join('\n');
  const origemItems = origem.map(item => `    \\item ${escapeLatex(item)}`).join('\n');

  return `
\\subsection*{D.${numero}. ${escapeLatex(titulo)}}

\\begin{minipage}[t]{0.48\\textwidth}
\\textbf{Potenciais consequências}
\\begin{itemize}[leftmargin=*,itemsep=1pt,parsep=0pt,topsep=2pt]
${potenciaisItems}
\\end{itemize}
\\end{minipage}
\\hfill
\\begin{minipage}[t]{0.48\\textwidth}
\\textbf{Origem}
\\begin{itemize}[leftmargin=*,itemsep=1pt,parsep=0pt,topsep=2pt]
${origemItems}
\\end{itemize}
\\end{minipage}

`;
}

/**
 * Gera a Seção B: Normas e Abreviações
 */
export function gerarSecaoBNormasAbreviacoes(): string {
  return `
\\newpage

\\noindent\\section*{Seção B - Normas e Abreviações}
\\addcontentsline{toc}{section}{B. Normas e Abreviações}
\\label{sec:normas-abreviacoes}

${gerarSubsecaoNormasLegislacao()}
${gerarSubsecaoAbreviacoes()}
`;
}

/**
 * Gera a subseção B.1: Normas e legislação
 */
function gerarSubsecaoNormasLegislacao(): string {
  return `
\\subsection*{B.1. Normas e legislação}
\\addcontentsline{toc}{subsection}{B.1. Normas e legislação}

\\vspace{0.3cm}


\\begin{itemize}[leftmargin=*,itemsep=2pt,parsep=0pt,topsep=4pt]
\\fontsize{9pt}{10.8pt}\\selectfont
\\item ${formatarNorma('NR-12 – Segurança de Máquinas e Equipamentos')}
\\item ${formatarNorma('ABNT NBR ISO 12100:2013 - Segurança de máquinas - Princípios gerais de projeto - Apreciação e redução de riscos')}
\\item ${formatarNorma('ABNT ISO/TR 14121-2:2018 - Segurança de máquinas - Apreciação de riscos - Parte 2: Guia prático e exemplos de métodos')}
\\item ${formatarNorma('ABNT NBR 13970:1997 - Segurança de máquinas - Temperatura de superfícies acessíveis - Dados ergonômicos para estabelecer os valores limites de temperatura de superfícies aquecidas')}
\\item ${formatarNorma('ABNT NBR ISO 13851:2024 - Segurança de máquinas - Dispositivo de comando bimanual - Princípios para projeto e seleção')}
\\item ${formatarNorma('ABNT NBR 14153:2022 - Segurança de máquinas - Partes de sistemas de comando relacionados à segurança - Princípios gerais para projeto')}
\\item ${formatarNorma('ABNT NBR ISO 14118:2022 - Segurança de máquinas - Prevenção de partida inesperada')}
\\item ${formatarNorma('ABNT NBR IEC 60204-1:2020 - Segurança de máquinas - Equipamentos Elétricos de Máquinas - Parte 1:Requisitos Gerais')}
\\item ${formatarNorma('ABNT NBR 5410:2004 - Versão Corrigida: 2008 - Instalações elétricas de baixa tensão')}
\\item ${formatarNorma('ABNT NBR IEC 60529:2017 - Graus de proteção providos por invólucros (Códigos IP)')}
\\item ${formatarNorma('ABNT NBR IEC 61439-1:2016 - Versão Corrigida: 2017 - Conjuntos de manobras e controle de baixa tensão - Parte 1: Regras Gerais')}
\\item ${formatarNorma('ABNT NBR IEC 61439-3:2017 - Conjuntos de manobras e controle de baixa tensão - Parte 3: Quadro de distribuição destinado a ser utilizado por pessoas comuns (DBO)')}
\\item ${formatarNorma('ABNT NBR IEC 61439-6:2018 - Conjuntos de manobras e controle de baixa tensão - Parte 2: Sistemas de Linhas Elétricas Pré-Fabricadas')}
\\item ${formatarNorma('ABNT NBR ISO 13849-1:2019 - Segurança de máquinas - Partes de sistemas de comando relacionados à segurança - Parte 1: Princípios gerais para projeto')}
\\item ${formatarNorma('ABNT NBR ISO 13849-2:2019 - Segurança de máquinas - Partes de sistemas de comando relacionados à segurança - Parte 2: Validação')}
\\item ${formatarNorma('ABNT NBR ISO 13850:2021 - Segurança de máquinas - Função de Parada de Emergência - Princípios para o Projeto')}
\\item ${formatarNorma('ABNT NBR NM ISO 13854:2003 - Segurança de máquinas - Folgas mínimas para evitar esmagamento de partes do corpo humano')}
\\item ${formatarNorma('ABNT NBR ISO 13855:2013 - Segurança de máquinas - Posicionamento dos equipamentos de proteção com referência à aproximação de partes do corpo humano')}
\\item ${formatarNorma('ABNT NBR ISO 13857:2021 - Segurança de máquinas - Distâncias de segurança para impedir o acesso a zonas de perigo pelos membros superiores')}
\\item ${formatarNorma('ABNT NBR ISO 14119:2021 - Segurança de máquinas - Dispositivos de Intertravamento Associados às Proteções - Princípios de Projeto e Seleção')}
\\item ${formatarNorma('ABNT NBR ISO 14120:2023 - Segurança de máquinas - Proteções - Requisitos gerais para projeto, construção e seleção de proteções fixas e móveis')}
\\item ${formatarNorma('ABNT NBR ISO 14122:1:2023 - Segurança de máquinas - Meios de acesso permanecentes para máquinas - Parte 1: Escolha de meios fixos e requisitos gerais de acesso')}
\\item ${formatarNorma('ABNT NBR ISO 14122:2:2023 - Segurança de máquinas - Meios de acesso permanecentes para máquinas - Parte 2: Plataformas de trabalhos e passarelas')}
\\item ${formatarNorma('ABNT NBR ISO 14122:3:2023 - Segurança de máquinas - Meios de acesso permanecentes para máquinas - Parte 3: Escadas, escadas íngremes e guarda-corpos')}
\\item ${formatarNorma('ABNT NBR ISO 14122:4:2023 - Segurança de máquinas - Meios de acesso permanecentes para máquinas - Parte 4: Escadas verticais')}
\\item ${formatarNorma('ABNT ISO/TR 24119:2022 - Segurança de máquinas - Avaliação de mascaramento de defeitos em conexões em série de dispositivos de intertravamento associados a proteções com contatos elétricos livres de potencial')}
\\item ${formatarNorma('ABNT NBR ISO 3864-1:2013 - Símbolos gráficos - Cores e sinais de segurança - Parte 1: Princípios de design para sinais e marcações de segurança')}
\\item ${formatarNorma('ABNT NBR ISO 4413:2022 - Sistemas de fluídos hidráulicos - Regras gerais e requisitos de segurança para sistemas e seus componentes')}
\\item ${formatarNorma('ANBT NBR ISO 4414:2012 - Transmissão pneumática de potência - Regras gerais e requisitos de segurança para sistemas e seus componentes')}
\\end{itemize}


\\begin{itemize}[leftmargin=*,itemsep=2pt,parsep=0pt,topsep=4pt]
\\fontsize{9pt}{10.8pt}\\selectfont
\\item ${formatarNorma('ISO 19353:2019 - Safety of machinery - Fire prevention and fire protection')}
\\item ${formatarNorma('ISO 20607:2019 - Safety of machinery - Instruction handbook - General drafting principles')}
\\item ${formatarNorma('ISO 7010:2019 - Graphical symbols - Safety colors and safety signs - Registered safety signs')}
\\item ${formatarNorma('IEC 61496-1:2020 - Safety of machinery - Electro-sensitive protective equipment - Part 1: General requirements and tests')}
\\item ${formatarNorma('IEC 61496-2:2020 - Safety of machinery - Electro-sensitive protective equipment - Part 2: Requirements for equipment using active optoelectronic protective devices (AOPDs)')}
\\item ${formatarNorma('IEC 61496-3:2018 - Safety of machinery - Electro-sensitive protective equipment - Part 3: Particular requirements for active opto-electronic protective devices responsive to diffuse Reflection (AOPDDR)')}
\\item ${formatarNorma('IEC 62046:2018 - Safety of machinery - Application of protective equipment to detect the presence of person')}
\\item ${formatarNorma('IEC 61800-5-1 - Adjustable speed electrical power drive systems - Part 5-1: Safety requirements - Electrical, thermal and energy')}
\\item ${formatarNorma('IEC 61800-5-2 - Adjustable speed electrical power drive systems - Part 5-2: Safety requirements – Functional')}
\\end{itemize}

`;
}

/**
 * Gera a subseção B.2: Abreviações
 */
function gerarSubsecaoAbreviacoes(): string {
  return `
\\subsection*{B.2. Abreviações}
\\addcontentsline{toc}{subsection}{B.2. Abreviações}

\\begin{description}[leftmargin=2.5cm,itemsep=2pt,parsep=0pt,topsep=4pt]
\\fontsize{9pt}{10.8pt}\\selectfont
\\item[ABNT:] ${escapeLatex('Associação Brasileira de Normas Técnicas')}
\\item[ALARP:] ${escapeLatex('(As low as reasonable possible): risco tão baixo, quanto razoavelmente possível')}
\\item[Cat:] ${escapeLatex('Categoria de Segurança equivalente a CC')}
\\item[CC:] ${escapeLatex('Categoria de controle')}
\\item[CE:] ${escapeLatex('Europe Community (Comunidade Europeia)')}
\\item[EN:] ${escapeLatex('European Standards (Norma Europeia)')}
\\item[FAT:] ${escapeLatex('(Factory Acceptance Test): Teste de aceitação da fábrica, trata-se de testes realizados na máquina nas instalações do OEM antes de enviar a máquina para as instalações do cliente')}
\\item[IEC:] ${escapeLatex('Comissão Internacional de Eletrotécnica')}
\\item[ISO:] ${escapeLatex('Organização Internacional de Normalização')}
\\item[NBR:] ${escapeLatex('Norma Brasileira de Referência')}
\\item[NR:] ${escapeLatex('Norma Regulamentadora')}
\\item[PL:] ${escapeLatex('Nível de Desempenho')}
\\item[PLr:] ${escapeLatex('Nível de desempenho requerido')}
\\item[DC:] ${escapeLatex('(Diagnostic Cover): Cobertura de diagnóstico')}
\\item[MTTFd:] ${escapeLatex('(Mean Time to Dangerous Failure): Tempo Médio para falha perigosa')}
\\item[SAT:] ${escapeLatex('(Site Acceptance Test): Teste de aceitação do local, Trata-se de testes realizados na máquina nas instalações do cliente durante as atividades de comissionamento')}
\\item[SRP/CS:] ${escapeLatex('Safety-Related Parts of Control Systems (Partes de Sistemas de Comando Relacionadas à Segurança)')}
\\end{description}

`;
}

/**
 * Gera a Seção 2: Informações Gerais para Apreciação de Risco
 */
function gerarSecao2InformacoesGerais(): string {
  return `
\\newpage

\\noindent\\section*{Seção C - INFORMAÇÕES GERAIS PARA APRECIAÇÃO DE RISCO}
\\addcontentsline{toc}{section}{C. Informações Gerais para Apreciação de Risco}
\\label{sec:informacoes-gerais}

\\subsection*{C.1. Obrigações legais}
\\addcontentsline{toc}{subsection}{C.1. Obrigações legais}

A Norma Regulamentadora NR12 - Segurança no Trabalho em Máquinas e Equipamentos e seus anexos definem referências técnicas, princípios fundamentais e medidas de proteção para garantir a saúde e integridade física dos trabalhadores e estabelece requisitos mínimos para prevenção de acidentes e doenças do trabalho nas fases de projeto e de utilização de máquinas e equipamentos de todos os tipos, e ainda à sua fabricação, importação, comercialização, exposição e cessão a qualquer título, em todas as atividades econômicas, sem prejuízo da observância do disposto nas demais Normas Regulamentadoras - NR aprovadas pela Portaria nº 3.214, de 8 de junho de 1978, nas normas técnicas vigentes e, na ausência ou omissão destas, nas normas internacionais aplicáveis.

A Diretiva Europeia de Máquinas 2006/42/EC é uma versão revisada da Diretiva Europeia de Máquinas, cuja primeira versão foi adotada em 1989. A nova Diretiva é aplicável desde 29 de dezembro de 2009. A Diretiva tem o objetivo duplo de harmonizar a saúde e os requisitos de segurança aplicáveis às máquinas na base de alto nível de proteção de saúde e segurança, enquanto assegura a circulação livre de máquinas no mercado da Comunidade Europeia. A Diretiva Europeia de Máquinas revisada não introduz mudanças radicais comparadas com as versões anteriores. Isso deixa claro e consolida as provisões das Diretivas com o objetivo de melhorar sua aplicação prática.

A Diretiva Europeia de Máquinas estipula que as máquinas não devem representar um risco (apreciação de risco de acordo com ABNT NBR ISO 12100:2013). Assumindo que não há risco zero na tecnologia, o objetivo é alcançar um risco residual aceitável. Se a segurança é dependente dos sistemas de controle, esses devem ser projetados para que a probabilidade de erros funcionais seja suficientemente baixa.

O procedimento de apreciação de riscos é descrito com os seus requisitos de saúde e segurança essenciais relacionados ao projeto e construção das máquinas, o qual pode ser encontrado no anexo I da Diretiva Europeia de Máquinas como segue:

O fabricante de máquinas ou seu representante autorizado deve assegurar que a apreciação de riscos é cumprida a fim de determinar os requisitos de saúde e segurança que são aplicados às máquinas. As máquinas devem ser projetadas e construídas levando em consideração os resultados da apreciação de riscos.

Pelo processo interativo de apreciação de riscos e redução de riscos, o fabricante ou seu representante autorizado devem:

\\begin{itemize}
    \\item Determinar os limites da máquina, os quais incluem o uso previsto e qualquer mau uso

previsível;
    \\item Identificar perigos que possam ser gerados pela máquina e situações associadas ao

perigo;
    \\item Estimar os riscos, levando em consideração a severidade de possíveis lesões e danos à

saúde e a probabilidade de seu acontecimento;
    \\item Avaliar os riscos, visando determinar quando a redução de risco é requerida, de

acordo com os objetivos da Norma Regulamentadora NR 12 - Segurança no Trabalho em Máquina e Equipamentos;
    \\item Eliminar os perigos ou reduzir os riscos associados com os perigos por aplicação de

medidas protetoras;
\\end{itemize}

\\subsection*{C.2. Procedimento para elaboração deste documento de Apreciação de Riscos}
\\addcontentsline{toc}{subsection}{C.2. Procedimento para elaboração deste documento de Apreciação de Riscos}

\\subsubsection*{C.2.1. Definir os limites de uso da máquina (ver 5.3.2, ABNT NBR 12100:2013);}
\\addcontentsline{toc}{subsubsection}{C.2.1. Definir os limites de uso da máquina (ver 5.3.2, ABNT NBR 12100:2013);}

Limites de uso incluem o uso devido da máquina bem como a má utilização de formas razoavelmente previsíveis. Aspectos a serem levados em consideração incluem:

\\begin{itemize}
    \\item Os diferentes modos de operação e diferentes procedimentos de intervenção para os usuários, incluindo intervenções exigidas pela má utilização da máquina;
    \\item O uso da máquina (por exemplo, industrial, não industrial e doméstico) por pessoas identificadas por gênero, idade, mão de uso dominante, ou habilidades físicas limitadas (visual, incapacidade auditiva, tamanho, força, etc);
    \\item Os níveis antecipados de treinamento, experiência ou habilidade do usuário, incluindo:

    \\begin{itemize}
        \\item Operadores,
        \\item Equipe de manutenção ou técnicos,
        \\item Aprendizes e treinandos, e
        \\item Público em geral;
    \\end{itemize}
    \\item Exposição de outras pessoas aos perigos associados à máquina, quando isto possa ser razoavelmente previsto:

    \\begin{itemize}
        \\item Pessoas que provavelmente possuam uma boa noção dos perigos específicos, tais como operadores de máquinas adjacentes;
        \\item Pessoas que provavelmente possuam uma pouca noção dos perigos específicos, mas que provavelmente tem conhecimento dos procedimentos de segurança do local, rotas autorizadas, etc., tais como, pessoal de administração;
        \\item Pessoas que possuam uma noção muito pequena dos perigos da máquina ou dos procedimentos de segurança, tais como, visitantes ou pessoas do público em geral, incluindo crianças.
    \\end{itemize}
\\end{itemize}

Caso as informações específicas observadas nos itens anteriores, não estejam disponíveis, o fabricante deve levar em consideração informações gerais sobre a população usuária (por exemplo, dados antropométricos apropriados).

\\subsubsection*{C.2.2. Definir os limites de espaço da máquina (ver 5.3.3, ABNT NBR 12100:2013);}
\\addcontentsline{toc}{subsubsection}{C.2.2. Definir os limites de espaço da máquina (ver 5.3.3, ABNT NBR 12100:2013);}

Aspectos a serem considerados para determinação dos limites de espaço incluem:

\\begin{itemize}
    \\item Cursos de movimento;
    \\item Espaços destinados a pessoas que interagem com a máquina, tanto em operação como manutenção;
    \\item Interação humana tal como a interface homem-máquina, e;
    \\item Conexão da máquina com as fontes de suprimento de energia.
\\end{itemize}

\\subsubsection*{C.2.3. Definir os limites de tempo da máquina (ver 5.3.4, ABNT NBR 12100:2013);}
\\addcontentsline{toc}{subsubsection}{C.2.3. Definir os limites de tempo da máquina (ver 5.3.4, ABNT NBR 12100:2013);}

Aspectos a serem considerados para determinação dos limites de tempo incluem:

\\begin{itemize}
    \\item A vida útil da máquina e/ou de alguns de seus componentes (ferramental, partes que podem se desgastar, componentes eletromecânicos, etc.), levando-se em consideração o uso devido da máquina e a má utilização de forma razoavelmente previsível, e;
    \\item Intervalos de serviço recomendados.
\\end{itemize}

\\subsubsection*{C.2.4. Definir outros limites da máquina (ver 5.3.5, ABNT NBR 12100:2013);}
\\addcontentsline{toc}{subsubsection}{C.2.4. Definir outros limites da máquina (ver 5.3.5, ABNT NBR 12100:2013);}

Exemplos de outros limites incluem:

\\begin{itemize}
    \\item Propriedades dos materiais a serem processados;
    \\item Limpeza e organização - O nível de limpeza exigido, e;
    \\item Meio ambiente - As condições máximas e mínimas de temperatura recomendadas, possibilidade de operação da máquina em ambientes externos ou internos, clima seco ou úmido, incidência direta da luz solar, tolerância à poeira e líquidos, etc.
\\end{itemize}

\\subsubsection*{C.2.5. Determinar todas as áreas perigosas;}
\\addcontentsline{toc}{subsubsection}{C.2.5. Determinar todas as áreas perigosas;}

Identificar o tipo da máquina, seu funcionamento e localizar as áreas onde os perigos se encontram.

\\subsubsection*{C.2.6. Identificar perigos;}
\\addcontentsline{toc}{subsubsection}{C.2.6. Identificar perigos;}

Identificar os perigos de acordo com a ABNT NBR ISO 12100:2013 descritos no capítulo 3 deste documento, e se possível, utilizando normas específicas da máquina, inclusive normas "tipo C" devem ser consideradas.

\\subsubsection*{C.2.7. Estimar riscos de acordo com ABNT NBR ISO 12100:2013;}
\\addcontentsline{toc}{subsubsection}{C.2.7. Estimar riscos de acordo com ABNT NBR ISO 12100:2013;}

Estimar os riscos através da metodologia HRN (Hazard Rating Number), descrita no capítulo 4 deste documento.

\\subsubsection*{C.2.8. Avaliar os riscos e, se necessário, propor medidas para mitigação dos riscos com as seguintes medidas:}
\\addcontentsline{toc}{subsubsection}{C.2.8. Avaliar os riscos e, se necessário, propor medidas para mitigação dos riscos com as seguintes medidas:}

\\begin{itemize}
    \\item Segurança inerente através de projetos e medidas construtivas - Os projetos elétricos, mecânicos, hidráulicos devem prever a segurança intrínseca determinada por norma e incluir as medidas construtivas de segurança.
    \\item Medidas técnicas protetoras - Verificar a existência de componentes de segurança, tais como, proteções fixas e móveis, dispositivos de intertravamento, dispositivos de monitoramento e controle de segurança, entre outros.
    \\item Medidas informativas de uso - Aplicar medidas para adequação dos manuais de informação de segurança da máquina, procedimentos de segurança, treinamento de segurança e sinalização que estão correlacionados entre si.
\\end{itemize}

\\subsubsection*{C.2.9. Documentação de resultados (ver seção 7, ABNT NBR ISO 12100:2013);}
\\addcontentsline{toc}{subsubsection}{C.2.9. Documentação de resultados (ver seção 7, ABNT NBR ISO 12100:2013);}

A documentação deve demonstrar o procedimento que foi seguido e os resultados obtidos. Isto deve incluir, quando relevante, documentações referentes:

\\begin{itemize}
    \\item À máquina para a qual a apreciação de riscos tenha sido feita (por exemplo, especificações, limites, uso previsto);
    \\item A quaisquer premissas relevantes ou condições de contorno que tenham sido adotadas (cargas, forças, fatores de segurança, etc.);
    \\item Aos perigos e situações perigosas identificadas e os eventos perigosos considerados na apreciação de riscos;
    \\item Informações nas quais a apreciação de riscos tenha sido baseada (ver 5.2, ABNT NBR ISO 12100:2013):

    \\begin{itemize}
        \\item Dados utilizados e suas fontes (histórico de acidentes, experiência obtida com a redução de riscos aplicada a máquinas similares, etc.);
        \\item As incertezas relativas aos dados utilizados e seus impactos na análise dos riscos;
    \\end{itemize}
    \\item Aos objetivos de redução de riscos a serem atingidos pelas medidas de proteção;
    \\item Às medidas de proteção implementadas destinadas a eliminar os perigos ou reduzir os riscos;
    \\item Aos riscos residuais associados à máquina;
    \\item Ao resultado da apreciação de riscos (ver Figura 1);
    \\item A quaisquer formulários preenchidos durante a avaliação dos riscos.
\\end{itemize}

`;
}

/**
 * Gera a Seção 3: Exemplos de Perigos Associados com as Máquinas
 */
function gerarSecao3ExemplosPerigos(): string {
  return `
\\newpage

\\noindent\\section*{Seção D - EXEMPLOS DE PERIGOS ASSOCIADOS COM AS MÁQUINAS}
\\addcontentsline{toc}{section}{D. Exemplos de Perigos Associados com as Máquinas}
\\label{sec:exemplos-perigos}

Os perigos especificados, situações causadoras de perigo, eventos de perigos representam exemplos típicos e são extraídos de ABNT NBR ISO 12100:2013.

Para as seguintes áreas sujeitas a perigos, os exemplos não estão em nenhuma ordem de prioridade. No momento da identificação dos riscos todas as condições sujeitas aos perigos existentes na máquina devem ser consideradas.

${gerarTipoPerigo(1, 'Perigos Mecânicos',
    [
      'aceleração, desaceleração;',
      'cantos vivos;',
      'aproximação de um elemento móvel a uma parte fixa;',
      'corte de peças;',
      'elementos elásticos;',
      'queda de objetos;',
      'gravidade;',
      'altura a partir do solo;',
      'alta pressão;',
      'instabilidade;',
      'energia cinética;',
      'mobilidade da máquina;',
      'elementos móveis;',
      'elementos rotativos;',
      'superfície áspera, escorregadia;',
      'arestas cortantes;',
      'energia armazenada;',
      'vácuo.'
    ],
    [
      'atropelamentos;',
      'arremessos;',
      'esmagamento;',
      'corte e mutilação;',
      'segurar e prender;',
      'enroscar;',
      'fricção e abrasão;',
      'impacto;',
      'injeção;',
      'raspagem;',
      'escorregamento, tropeço e queda;',
      'perfuração;',
      'sufocamento.'
    ]
  )}

${gerarTipoPerigo(2, 'Perigos Elétricos',
    [
      'arcos;',
      'fenômenos eletromagnéticos;',
      'partes vivas;',
      'baixa rigidez dielétrica;',
      'partes vivas sob condições de falha;',
      'curto-circuito;',
      'radiação térmica.'
    ],
    [
      'queimadura;',
      'efeitos químicos;',
      'efeitos em implantes médicos;',
      'eletrocussão;',
      'queda ou arremesso;',
      'incêndio;',
      'projeção de fagulhas;',
      'choque.'
    ]
  )}

${gerarTipoPerigo(3, 'Perigos Térmicos',
    [
      'explosão;',
      'incêndio;',
      'objetos ou materiais com alta ou baixa temperatura;',
      'radiação proveniente de fontes quentes.'
    ],
    [
      'queimadura;',
      'desidratação;',
      'desconforto;',
      'congelamento;',
      'danos causados pela radiação de fontes quentes;',
      'escaldo.'
    ]
  )}

${gerarTipoPerigo(4, 'Perigos ligados a Ruído',
    [
      'fenômeno de cavitação;',
      'sistema de exaustão;',
      'vazamento de gás em alta velocidade;',
      'processos de produção (estampagem, corte, etc.);',
      'partes móveis;',
      'superfícies em atrito;',
      'peças rotativas desbalanceadas;',
      'ruídos pneumáticos;',
      'peças desgastadas.'
    ],
    [
      'desconforto;',
      'perda de consciência;',
      'perda de equilíbrio;',
      'perda permanente de audição;',
      'estresse;',
      'zumbido;',
      'cansaço;',
      'outros como (por exemplo mecânicos, elétricos) em decorrência da interferência em comunições ou sinais acústicos.'
    ]
  )}

${gerarTipoPerigo(5, 'Perigos ligados a Vibração',
    [
      'fenômeno de cavitação;',
      'desalinhamento de partes móveis;',
      'equipamentos móveis;',
      'superfícies em atrito;',
      'peças rotativas desbalanceadas;',
      'equipamentos que vibram;',
      'peças desgastadas.'
    ],
    [
      'desconforto;',
      'morbidade lombar;',
      'disfunções neurológicas;',
      'disfunções osteoarticulares;',
      'traumas na coluna;',
      'disfunções vasculares.'
    ]
  )}

${gerarTipoPerigo(6, 'Perigos ligados a Radiação',
    [
      'fontes de radiação ionizante;',
      'radiações eletromagnéticas de baixa frequência;',
      'radiação ótica (infravermelho, visível ou ultravioleta), incluindo laser;',
      'radiações eletromagnéticas em radiofrequência.'
    ],
    [
      'queimadura;',
      'danos nos olhos e pele;',
      'efeitos na capacidade reprodutiva;',
      'mutações;',
      'dor de cabeça, insônia, etc;',
      'efeitos cancerígenos.'
    ]
  )}

${gerarTipoPerigo(7, 'Materiais, Substâncias perigosas',
    [
      'aerossóis;',
      'agentes biológicos e microbiológicos (virais ou bacterianos);',
      'combustíveis;',
      'poeira;',
      'explosivos;',
      'fibras;',
      'inflamáveis;',
      'fluídos;',
      'fumos;',
      'gases;',
      'misturas;',
      'oxidantes.'
    ],
    [
      'dificuldades para respiração, asfixia;',
      'câncer;',
      'corrosão;',
      'efeitos na capacidade reprodutiva;',
      'explosões;',
      'fogo;',
      'infecções;',
      'mutações;',
      'envenenamento;',
      'sensibilização.'
    ]
  )}

${gerarTipoPerigo(8, 'Perigos Ergonômicos',
    [
      'acesso;',
      'projeto ou localização de indicadores, indicadores e painéis;',
      'projeto, localização ou identificação de dispositivos de comando;',
      'esforços;',
      'cintilação, brilho, sombra, efeito estroboscópio;',
      'luz local;',
      'sobrecarga mental, ociosidade;',
      'postura;',
      'atividade repetitiva;',
      'visibilidade.'
    ],
    [
      'desconforto;',
      'fadiga;',
      'distúrbios músculo esqueléticos;',
      'estresse;',
      'outros como (por exemplo mecânicos, elétricos) em decorrência de erros humanos.'
    ]
  )}

${gerarTipoPerigo(9, 'Perigos associados com o ambiente no qual a máquina é utilizada',
    [
      'poeira ou neblina;',
      'pertubação eletromagnética;',
      'descargas atmosféricas;',
      'umidade;',
      'poluição;',
      'neve;',
      'temperatura;',
      'água;',
      'vento;',
      'falta de oxigênio.'
    ],
    [
      'queimadura;',
      'doenças brandas;',
      'queda ou escorregamento;',
      'asfixia;',
      'qualquer outra consequência do efeito causado por fontes de perigos da máquina ou partes da mesma.'
    ]
  )}

${gerarTipoPerigo(10, 'Combinação de Perigos',
    [
      'por exemplo, atividades repetitivas + esforços + ambientes em alta temperatura.'
    ],
    [
      'por exemplo, desidratação, perda de consciência e ataque cardíaco.'
    ]
  )}
\\vspace{0.7cm}

ABNT NBR ISO 12100:2013: Exemplos de perigos, situações perigosas e eventos perigosos

`;
}

/**
 * Gera a Tabela 1: Comparação entre nível de desempenho e nível de integridade de segurança
 */
function gerarTabela1ComparacaoPL(): string {
  return `
\\begin{table}[H]
\\centering
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

% Definição de cores
\\definecolor{azulEscuroTabela}{RGB}{0,45,95}
\\definecolor{verdeClaroTabela}{RGB}{200,255,200}
\\definecolor{amareloTabela}{RGB}{255,255,0}
\\definecolor{laranjaTabela}{RGB}{255,150,0}
\\definecolor{vermelhoTabela}{RGB}{230,0,0}
\\definecolor{vermelhoEscuroTabela}{RGB}{180,0,0}

\\begin{tabular}{|>{\\centering\\arraybackslash}p{5cm}|
                >{\\centering\\arraybackslash}p{5cm}|
                >{\\centering\\arraybackslash}p{4cm}|}
\\hline
\\rowcolor{azulEscuroTabela}
{\\color{white}\\textbf{ISO 13849-1 Nível de desempenho (PL)}} &
{\\color{white}\\textbf{Probabilidade de falha perigosa por hora (PFHd)}} &
{\\color{white}\\textbf{IEC 62061 Nível de integridade de segurança (SIL)}} \\\\
\\hline
\\rowcolor{verdeClaroTabela}
a &
$10^{-5} \\leq \\text{PFHd} < 10^{-4}$ &
Nenhum \\\\
\\hline
\\rowcolor{amareloTabela}
b &
$3 \\times 10^{-6} \\leq \\text{PFHd} < 10^{-5}$ &
1 \\\\
\\hline
\\rowcolor{laranjaTabela}
c &
$10^{-6} \\leq \\text{PFHd} < 3 \\times 10^{-6}$ &
1 \\\\
\\hline
\\rowcolor{vermelhoTabela}
d &
$10^{-7} \\leq \\text{PFHd} < 10^{-6}$ &
2 \\\\
\\hline
\\rowcolor{vermelhoEscuroTabela}
{\\color{white}e} &
{\\color{white}$10^{-8} \\leq \\text{PFHd} < 10^{-7}$} &
{\\color{white}3} \\\\
\\hline
\\end{tabular}
\\caption{Comparação entre nível de desempenho e nível de integridade de segurança}
\\label{tab:comparacao-pl}
\\end{table}

`;
}

/**
 * Gera a Seção 4: Avaliação de Riscos e Propostas de Redução de Riscos
 */
function gerarSecao4AvaliacaoRiscos(): string {
  return `
\\newpage

\\noindent\\section*{Seção E - AVALIAÇÃO DE RISCOS E PROPOSTAS DE REDUÇÃO DE RISCOS}
\\addcontentsline{toc}{section}{E. Avaliação de Riscos e Propostas de Redução de Riscos}
\\label{sec:avaliacao-riscos}

Durante a avaliação de riscos das máquinas, será usada a ABNT NBR ISO 12100:2013.

Na ABNT NBR ISO 12100:2013, as bases e princípios de diretrizes gerais pedem uma análise de riscos.

Além disso, a norma ABNT NBR ISO 12100:2013 especifica princípios de diretrizes técnicas, a fim de dar suporte aos projetistas técnicos para projetar máquinas seguras.

A avaliação de riscos das máquinas e a determinação do nível de desempenho requerido (PLr) é feito utilizando a ABNT NBR ISO 13849-1:2019 e comparada com o nível de integridade de segurança (SIL) especificado na norma IEC 62061:2015 utilizando o valor PFH para quantificar as probabilidades de falhas perigosas por hora conforme tabela abaixo:

${gerarTabela1ComparacaoPL()}

O Brasil adota as categorias de segurança B, 1, 2, 3 e 4 segundo ABNT NBR

14153:2022.

\\subsection*{E.1. Critérios para Avaliação de Riscos}
\\addcontentsline{toc}{subsection}{E.1. Critérios para Avaliação de Riscos}

Os critérios de avaliação de riscos usados pela SCHMERSAL são tomados da norma ABNT NBR ISO 12100:2013.

De acordo com ABNT NBR ISO 12100:2013 uma estimativa de risco e avaliação será providenciada para cada perigo determinado. As fases da vida de uma máquina nas quais os perigos foram identificados ou foram considerados significantes foram consideradas neste documento.

A identificação dos perigos é realizada por partes das máquinas onde as tarefas são realizadas.

\\subsection*{E.2. Categorização de Segurança}
\\addcontentsline{toc}{subsection}{E.2. Categorização de Segurança}
\\label{sec:categoria-desempenho}

Conforme a norma ABNT NBR ISO 13849-1:2019, nas partes da máquina serão detalhadas, as informações de Severidade do ferimento, Frequência e/ou tempo de exposição e Possibilidade de evitar o dano, determinado assim o nível de desempenho requerido (PLr), SIL (Safety Integrity Level, ou Nível de integridade de segurança) e Categoria de Segurança (B,1,2,3 ou 4).

\\subsubsection*{E.2.1. Severidade S1 e S2}
\\addcontentsline{toc}{subsubsection}{E.2.1. Severidade S1 e S2}

A severidade do ferimento pode ser estimada considerando-se a gravidade de lesões ou danos à saúde, que podem ser leves, graves ou fatais, e a extensão do dano, causado à uma pessoa ou várias pessoas.

Ao realizar-se uma avaliação de riscos, o risco a ser considerado para cada perigo, deve ser aquele relativo à severidade mais provável, por sua vez, referente ao dano mais provável, entretanto, a maior gravidade previsível deve também ser levada em consideração, mesmo que a probabilidade de tal ocorrência não seja alta.

O anexo A da ABNT NBR ISO 13849-1:2019 determina a severidade em duas partes S1 e S2 onde, as consequências normais de cura devem ser levadas em consideração na determinação de S1 e S2, por exemplo, contusões e/ou lacerações, sem complicações devem ser classificadas como S1, enquanto uma amputação ou morte deve ser classificada como S2.

\\subsubsection*{E.2.2. Frequência F1 e F2}
\\addcontentsline{toc}{subsubsection}{E.2.2. Frequência F1 e F2}

Conforme o anexo A da ABNT NBR ISO 13849-1:2019, um período de tempo geralmente válido para a escolha do parâmetro F1 ou F2 não pode ser especificado. Entretanto, a seguinte explicação pode ajudar a tomar a decisão correta, em caso de dúvida.

F2 deve ser selecionado, se a pessoa estiver, frequentemente ou continuadamente, exposta ao perigo.

É irrelevante se a mesma pessoa ou pessoas diferentes estiverem expostas ao perigo em sucessivas ocasiões, como, por exemplo, para a utilização de elevadores.

O período de exposição ao perigo deve ser avaliado com base no valor médio observado, com relação ao período total de utilização do equipamento. Por exemplo, se for necessário acessar regularmente as ferramentas da máquina durante sua operação cíclica, para a alimentação e movimentação de peças, F2 deve ser selecionado. Se o acesso somente for necessário de tempo em tempo, pode-se selecionar F1.

\\subsubsection*{E.2.3. Possibilidade de evitar o dano P1 e P2}
\\addcontentsline{toc}{subsubsection}{E.2.3. Possibilidade de evitar o dano P1 e P2}

Quando um perigo aparece, é importante saber se ele pode ser reconhecido e quando pode ser evitado, antes de levar a um acidente. Por exemplo, uma importante consideração é se o perigo pode ser diretamente identificado por suas características físicas ou por meios técnicos, por exemplo, indicadores. Outro aspecto importante que influencia a seleção do parâmetro P inclui, por exemplo:

\\begin{itemize}
    \\item Operação com ou sem supervisão;
    \\item Operação por especialistas ou por não profissionais;
    \\item Velocidade com que o perigo aparece, por exemplo, rapidamente ou lentamente;
    \\item Possibilidades de se evitar o perigo, por exemplo, por fuga ou por intervenção de terceiros;
    \\item Experiências práticas de segurança relativas ao processo.
\\end{itemize}

Quando uma situação de perigo ocorre, P1 deve apenas ser selecionado se houver uma chance real de se evitar um acidente ou reduzir significativamente o seu efeito. P2 deve ser selecionado se praticamente não houver chance de se evitar o perigo, conforme anexo A da ABNT NBR ISO 13849-1:2019.

`;
}

/**
 * Gera as seções iniciais do documento (Seção B, C, D e E)
 */
export function gerarSecoesIniciais(): string {
  let latex = '';
  latex += gerarSecaoBNormasAbreviacoes();
  latex += gerarSecao2InformacoesGerais();
  latex += gerarSecao3ExemplosPerigos();
  latex += gerarSecao4AvaliacaoRiscos();
  return latex;
}

